// Product gallery: thumbnails and prev/next arrows scroll the main snap track; the visible slide marks its thumbnail current and updates the counter.
class SGallery extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbRow = this.querySelector('[data-gallery-thumbs]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.current = 0;
    if (!this.track) return;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)), { signal });
    });
    this.prev?.addEventListener('click', () => this.goTo(this.current - 1), { signal });
    this.next?.addEventListener('click', () => this.goTo(this.current + 1), { signal });

    this.track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      this.goTo(this.current + (forward ? 1 : -1));
    }, { signal });

    this.track.addEventListener('scroll', () => {
      const index = Math.round(Math.abs(this.track.scrollLeft) / Math.max(1, this.track.clientWidth));
      if (index !== this.current) this.setCurrent(index);
    }, { signal, passive: true });

    this.setCurrent(0);
  }

  get count() {
    return this.track?.children.length ?? 0;
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(this.count - 1, index));
    const slide = this.track.children[clamped];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: this.reduce ? 'auto' : 'smooth' });
    this.setCurrent(clamped);
  }

  setCurrent(index) {
    this.current = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= this.count - 1;
    if (this.counter?.dataset.template) {
      this.counter.textContent = this.counter.dataset.template.replace('[current]', String(index + 1));
    }

    // Keep the active thumbnail visible without scrolling the page vertically.
    const thumb = this.thumbs[index];
    if (thumb && this.thumbRow) {
      const rowStart = this.thumbRow.scrollLeft;
      const rowEnd = rowStart + this.thumbRow.clientWidth;
      const thumbStart = thumb.offsetLeft - this.thumbRow.offsetLeft;
      const thumbEnd = thumbStart + thumb.offsetWidth;
      if (thumbStart < rowStart || thumbEnd > rowEnd) {
        this.thumbRow.scrollTo({ left: thumbStart - (this.thumbRow.clientWidth - thumb.offsetWidth) / 2, behavior: this.reduce ? 'auto' : 'smooth' });
      }
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('s-gallery')) customElements.define('s-gallery', SGallery);
